import { skipToken, useQuery, useMutation } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { orpc, rpcClient, useInvalidateRpcQueryFn } from '@/api/rpc/client';
import { useToast } from '@/context/ToastContext';

function useInvalidateEmailIngest() {
  return useInvalidateRpcQueryFn((o) => [
    o.org.config.emailIngest.list.key(),
    o.org.config.emailIngest.get.key(),
  ]);
}

export function useEmailIngestConfigsList() {
  return useQuery({
    ...orpc.org.config.emailIngest.list.queryOptions({ input: undefined }),
  });
}

export function useEmailIngestConfig(appRegistrationId: string | undefined) {
  return useQuery({
    ...orpc.org.config.emailIngest.get.queryOptions({
      input: appRegistrationId ? { appRegistrationId } : skipToken,
    }),
    enabled: !!appRegistrationId,
  });
}

export function useCreateEmailIngestConfig(options?: {
  onSuccess?: () => void;
}) {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (
      input: Parameters<typeof rpcClient.org.config.emailIngest.create>[0],
    ) => rpcClient.org.config.emailIngest.create(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Email registration config created',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to create config',
        color: 'error',
      });
    },
  });
}

export function useUpdateEmailIngestConfig(options?: {
  onSuccess?: (
    result: Awaited<ReturnType<typeof rpcClient.org.config.emailIngest.update>>,
  ) => void;
}) {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();
  const gt = useGT();

  return useMutation({
    mutationFn: (
      input: Parameters<typeof rpcClient.org.config.emailIngest.update>[0],
    ) => rpcClient.org.config.emailIngest.update(input),
    onSuccess: (result) => {
      const failures = result.subscriptionFailures ?? [];
      if (failures.length > 0) {
        showToast({
          title: gt('Credentials saved with webhook warnings', {
            context:
              'Azure email registration toast title when credentials saved but webhook re-subscribe failed',
          }),
          message: gt(
            'Saved, but {count} mailbox webhook(s) failed to re-subscribe: {mailboxes}. Check credentials / Graph permissions and retry.',
            {
              context:
                'Azure email registration toast message listing mailboxes that failed webhook re-subscribe',
              count: String(failures.length),
              mailboxes: failures.map((f) => f.mailboxUpn).join(', '),
            },
          ),
          color: 'warning',
        });
      } else {
        showToast({
          title: gt('Success', {
            context: 'Azure email registration update toast title',
          }),
          message: gt('Email registration config updated', {
            context: 'Azure email registration update toast message',
          }),
          color: 'success',
        });
      }
      invalidate({});
      options?.onSuccess?.(result);
    },
    onError: (error: Error) => {
      showToast({
        title: gt('Error', {
          context: 'Azure email registration update error toast title',
        }),
        message:
          error.message ||
          gt('Failed to update config', {
            context: 'Azure email registration update error toast fallback',
          }),
        color: 'error',
      });
    },
  });
}

export function useDeleteEmailIngestConfig(options?: {
  onSuccess?: () => void;
}) {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { appRegistrationId: string }) =>
      rpcClient.org.config.emailIngest.delete(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Email registration config deleted',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to delete config',
        color: 'error',
      });
    },
  });
}

export function useToggleUpn() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: {
      appRegistrationId: string;
      mailboxId: string;
      enabled: boolean;
    }) => rpcClient.org.config.emailIngest.toggleUpn(input),
    onSuccess: (_data, variables) => {
      showToast({
        title: 'Success',
        message: `Mailbox ${variables.enabled ? 'enabled' : 'disabled'}`,
        color: 'success',
      });
      invalidate({});
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to toggle mailbox',
        color: 'error',
      });
    },
  });
}

export function useAddUpn() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: {
      appRegistrationId: string;
      upn: string;
      name?: string | null;
      permission: 'read' | 'write';
    }) => rpcClient.org.config.emailIngest.addUpn(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Mailbox added',
        color: 'success',
      });
      invalidate({});
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to add mailbox',
        color: 'error',
      });
    },
  });
}

export function useUpdateUpn(options?: { onSuccess?: () => void }) {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: {
      appRegistrationId: string;
      mailboxId: string;
      upn?: string;
      name?: string | null;
    }) => rpcClient.org.config.emailIngest.updateUpn(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Mailbox updated',
        color: 'success',
      });
      invalidate({});
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to update mailbox',
        color: 'error',
      });
    },
  });
}

export function useRemoveUpn() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { appRegistrationId: string; mailboxId: string }) =>
      rpcClient.org.config.emailIngest.removeUpn(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Mailbox removed',
        color: 'success',
      });
      invalidate({});
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to remove mailbox',
        color: 'error',
      });
    },
  });
}

export function useRefreshUpn() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { appRegistrationId: string; mailboxId: string }) =>
      rpcClient.org.config.emailIngest.refreshUpn(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Mailbox webhook refreshed',
        color: 'success',
      });
      invalidate({});
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to refresh mailbox webhook',
        color: 'error',
      });
    },
  });
}

export function useRefreshWebhooks() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { appRegistrationId: string }) =>
      rpcClient.org.config.emailIngest.refreshWebhooks(input),
    onSuccess: () => {
      showToast({
        title: 'Success',
        message: 'Webhooks refreshed',
        color: 'success',
      });
      invalidate({});
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to refresh webhooks',
        color: 'error',
      });
    },
  });
}

export function useRequeueMissedEmails() {
  const invalidate = useInvalidateEmailIngest();
  const { showToast } = useToast();

  return useMutation({
    mutationFn: (input: { appRegistrationId: string; lookbackHours: number }) =>
      rpcClient.org.config.emailIngest.requeueMissedEmails(input),
    onSuccess: (result) => {
      invalidate({});
      if (result.status === 'busy') {
        showToast({
          title: 'Already running',
          message:
            result.message ||
            'A lookback requeue is already in progress for this config',
          color: 'warning',
        });
        return;
      }
      if (result.status === 'noop') {
        showToast({
          title: 'Nothing to scan',
          message: result.message || 'No active mailboxes to requeue',
          color: 'warning',
        });
        return;
      }
      if (result.status === 'completed_with_errors') {
        showToast({
          title: 'Lookback requeue finished with errors',
          message:
            result.message ||
            `Requeued ${result.emailsQueued} missed email(s), but some chunks/mailboxes failed`,
          color: 'warning',
        });
        return;
      }
      showToast({
        title: 'Lookback requeue finished',
        message: `Requeued ${result.emailsQueued} missed email(s)`,
        color: result.partialChunks > 0 ? 'warning' : 'success',
      });
    },
    onError: (error: Error) => {
      showToast({
        title: 'Error',
        message: error.message || 'Failed to requeue missed emails',
        color: 'error',
      });
    },
  });
}
